import React from 'react';
import { ColorFacetTerms, RangeFilterFacet, FacetTerms } from '@components';
import { findBaseColor } from '@utils';
import { Term, TermInterval, Facet } from '@services/product/interfaces';

interface FacetFiltersProps {
  facets: Facet[] | undefined;
  selectedTerms: Record<string, string[]>;
  selectTerm: (facet: string, term: string) => void;
  deleteTerm: (facet: string, term: string) => void;
  selectColor: (facet: string, color: string) => void;
  deleteColor: (facet: string, color: string) => void;
  isAWSEnv?: boolean;
}

export const FacetFilters: React.FC<FacetFiltersProps> = ({
  facets,
  selectedTerms,
  selectTerm,
  deleteTerm,
  selectColor,
  deleteColor,
  isAWSEnv = false
}) => {
  const facetLabelsByName: Record<string, string|undefined> = {};
  facets?.forEach(({ facet }) => {
    facetLabelsByName[facet] = facets.find(({ facet: facetAttribute }) =>
      facet.match(facetAttribute))?.displayName;
  });

  const renderFacetsForGCP = (): React.ReactElement[] | undefined => facets?.filter(item =>
    !item.facet.match('price') && item.displayName
  ).map(item => {
    const label = item.displayName;
    const { facet } = item;

    if (facet.match('color')) {
      const colors = [ ...new Set(item.value.terms.map(color => findBaseColor(color.term))) ];
      const selectedColors = selectedTerms[facet] ?
        [ ...new Set(selectedTerms[facet].map(color => findBaseColor(color))) ] :
        [];

      return <ColorFacetTerms
        key={ facet }
        label={ label }
        colors={ colors }
        selectedColors={ selectedColors }
        selectColor={ (color): void => selectColor(facet, color) }
        deleteColor={ (color): void => deleteColor(facet, color) }
      />;
    } else if ((item.value.terms[0] as TermInterval)?.interval) {
      const transformRangeFacetData = (term: TermInterval): { count: number; from: number; to: number } => ({
        count: term.count,
        from: term.interval.minimum,
        to: term.interval.maximum
      });

      const formattedRangeFacetData = (item.value.terms as TermInterval[])
        .map(term => transformRangeFacetData(term));

      return <RangeFilterFacet
        key={ facet }
        facet={ facet }
        label={ label }
        ranges={ formattedRangeFacetData }
        selectedTerms={ selectedTerms[facet] ?? [] }
        selectTerm={ (term): void => selectTerm(facet, term) }
        deleteTerm={ (term): void => deleteTerm(facet, term) }
        formatRangeValue={ (from: number, to: number): string => `${Math.ceil(from)} - ${Math.ceil(to)} min` }
      />;
    } else {
      return <FacetTerms
        key={ facet }
        label={ label }
        terms={ item.value.terms as Term[] }
        termsType={ item.value.dataType }
        selectedTerms={ selectedTerms[facet] ?? [] }
        selectTerm={ (term): void => selectTerm(facet, term) }
        deleteTerm={ (term): void => deleteTerm(facet, term) }
        countEntity='products'/>;
    }
  });

  const renderFacetsForAWS = (): React.ReactElement[] | undefined => facets?.filter(item =>
    !item.facet.match('price.centAmount') && item.displayName
  ).map(item => {
    const label = facetLabelsByName[item.facet] ?? item.displayName;

    if (label.match('Color')) {
      const colors = [ ...new Set(item.value.terms.map(color => findBaseColor(color.term))) ];
      const selectedColors = selectedTerms[item.facet] ?
        [ ...new Set(selectedTerms[item.facet].map(color => findBaseColor(color))) ] :
        [];

      return <ColorFacetTerms
        key={ item.facet }
        colors={ colors }
        selectedColors={ selectedColors }
        selectColor={ (color): void => selectColor(item.facet, color) }
        deleteColor={ (color): void => deleteColor(item.facet, color) }
      />;
    } else {
      return <FacetTerms
        key={ item.facet }
        label={ label }
        terms={ item.value.terms as Term[] }
        termsType={ item.value.dataType }
        selectedTerms={ selectedTerms[item.facet] ?? [] }
        selectTerm={ (term): void => selectTerm(item.facet, term) }
        deleteTerm={ (term): void => deleteTerm(item.facet, term) }
        countEntity='products'/>;
    }
  });

  return <>{isAWSEnv ? renderFacetsForAWS() : renderFacetsForGCP()}</>;
};

export default FacetFilters;
